/**
 * NPC Briefing helpers
 *
 * Shared helpers for dialogue scenes:
 * - resolveSpeaker / buildPortraitStyle / applyPortraitTuning / getHubBg (used by MainMenuScreen)
 * - runDialogueScene-backed showDebriefDialogue and showDecisionScene (post-combat overlays)
 * - playVoiceover / stopVoiceover (per-line MP3 narration)
 *
 * The initial chapter briefing is rendered inline by MainMenuScreen.showBriefingInline,
 * not here — it shares the helpers above for consistency.
 */

import { getFullSpriteUrl, getPortraitUrl, getPortraitTuning, PLAYER_SPRITES, CB } from '../../combat/spriteConfig';
import { renderUnitPortrait, buildPortraitTransform } from './UnitPortrait';
import {
  type NpcBriefing,
  type DialogueLine,
  type PartyCondition,
  type DecisionConfig,
  type DecisionChoice,
} from '../../config/briefings';
import { StateManager } from '../../managers';
import { APIManager } from '../../managers/APIManager';
import { audioBus } from '../../audio/AudioBus';
import type { UnitData, ConfigData, PartyUnit } from '../../types';
import { VOICED_PARTY_VARIANTS } from '../../../../scripts/voiceover/cast';

/* ── Helpers ── */

export function getHubBg(bgPath?: string): string {
  const base = import.meta.env.BASE_URL ?? '/';
  const path = bgPath ?? 'bg/town_menu';
  return `${base}sprites/processed/${path}.webp${CB}`;
}

function getSpriteSrc(spriteKey: string): string {
  return getFullSpriteUrl(spriteKey) || getPortraitUrl(spriteKey) || '';
}

/** Collect every distinct NPC sprite key that any line in the dialogue uses,
 *  in first-appearance order. The fallback is briefing.spriteKey when a line
 *  omits its own. Used to render multiple NPCs side-by-side. */
export function collectNpcSpriteKeys(briefing: NpcBriefing, dialogue: DialogueLine[]): string[] {
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const line of dialogue) {
    if (line.speaker !== 'npc') continue;
    const key = line.spriteKey || briefing.spriteKey || '';
    if (key && !seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
  }
  return keys;
}

/** Build the inner HTML for the NPC stage area. When there are 2+ distinct
 *  NPC sprites in the dialogue, render them as siblings so the player sees
 *  the whole cast on stage at once instead of a sprite swap. */
export function buildNpcStageHTML(npcKeys: string[], firstActiveKey: string): string {
  return npcKeys.map(key => {
    const src = getSpriteSrc(key);
    if (!src) return '';
    const scale = PLAYER_SPRITES[key]?.scale ?? 1;
    const transform = scale !== 1
      ? `transform: scaleX(-1) scale(${scale}); transform-origin: bottom center;`
      : '';
    const speaking = key === firstActiveKey ? ' is-speaking' : '';
    return `<img class="nb-npc-sprite${speaking}" data-sprite-key="${key}" src="${src}" alt="" draggable="false" style="${transform}" />`;
  }).join('');
}

/** Build inline CSS for portrait face-crop tuning. Delegates to the shared
 *  formula in UnitPortrait so the briefing/debrief crop matches the combat
 *  HUD and dev tuner exactly, regardless of container size. */
export function buildPortraitStyle(tuning: { scale?: number; offsetX?: number; offsetY?: number }): string {
  const hasScale = tuning.scale != null;
  const hasX = tuning.offsetX != null;
  const hasY = tuning.offsetY != null;
  if (!hasScale && !hasX && !hasY) return '';
  return `transform:${buildPortraitTransform(tuning)}`;
}

/** Apply portrait face-crop tuning to an img element. Same shared formula as
 *  renderUnitPortrait so the crop is identical across the HUD, dev tuner, and
 *  dialogue scenes. */
export function applyPortraitTuning(img: HTMLImageElement, tuning: { scale?: number; offsetX?: number; offsetY?: number }): void {
  img.style.transform = buildPortraitTransform(tuning);
  img.style.objectPosition = '';
}

/* ── Party strand resolution ── */

export interface ResolvedSpeaker {
  name: string;
  title: string;
  isParty: boolean;
  /** Sprite key for portrait rendering. NPC: from line/briefing. Party: from unit template or skin. */
  spriteKey?: string;
  /** Unit ID of the speaking party member (for per-member highlighting). */
  unitId?: string;
}

function getActiveParty(): PartyUnit[] {
  const unitData = StateManager.get('unitData') as UnitData | undefined;
  return unitData?.party ?? [];
}

function matchCondition(unit: PartyUnit, cond: PartyCondition): boolean {
  if (cond.templateId && unit.unitClass !== cond.templateId) return false;
  if (cond.race && unit.race !== cond.race) return false;
  if (cond.className && !unit.unitClass.includes(cond.className)) return false;
  return true;
}

function findMatchingStrand(cond: PartyCondition): PartyUnit | null {
  const party = getActiveParty();
  return party.find(u => matchCondition(u, cond)) ?? null;
}

/** True if the unit's race+gender has a cast party voice. Used to prefer
 *  voiced strands when picking who speaks a party line — silent strands are
 *  only chosen when the entire pool of candidates is uncast. */
function isUnitVoiced(unit: PartyUnit | null): boolean {
  if (!unit || !unit.race) return false;
  const configData = StateManager.get('configData') as ConfigData | undefined;
  const template = (configData as Record<string, unknown> | undefined)?.unitTemplates as
    Record<string, { gender?: string }> | undefined;
  const gender = template?.[unit.unitClass]?.gender;
  if (!gender) return false;
  return VOICED_PARTY_VARIANTS.has(`${unit.race}_${gender}`);
}

/** Resolve a party unit's sprite key from its template or active skin. */
function getPartyUnitSpriteKey(unit: PartyUnit): string {
  const configData = StateManager.get('configData') as ConfigData | undefined;
  const template = (configData as Record<string, unknown> | undefined)?.unitTemplates as
    Record<string, { spriteKey?: string }> | undefined;
  const skins = (configData as Record<string, unknown> | undefined)?.skins as
    Record<string, { spriteKey?: string }> | undefined;
  const skinKey = unit.activeSkinId && skins?.[unit.activeSkinId]?.spriteKey;
  return skinKey || template?.[unit.unitClass]?.spriteKey || unit.unitClass;
}

/** Resolve the voiceover variant key for a party speaker: "{race}_{gender}".
 *  Returns null when we can't determine race+gender (no unit, no template, etc.)
 *  — in which case we fall back to no voiceover for that line, which is fine
 *  since playback is purely additive. */
function getPartyVariantKey(unitId: string | undefined): string | null {
  if (!unitId) return null;
  const unit = getActiveParty().find(u => u.id === unitId);
  if (!unit) return null;
  const configData = StateManager.get('configData') as ConfigData | undefined;
  const template = (configData as Record<string, unknown> | undefined)?.unitTemplates as
    Record<string, { gender?: string }> | undefined;
  const gender = template?.[unit.unitClass]?.gender;
  if (!unit.race || !gender) return null;
  return `${unit.race}_${gender}`;
}

/** Build the voiceover variant key for a resolved briefing speaker. NPC lines
 *  return null (canonical filename has no variant suffix).
 *
 *  When a templateId variant fired (e.g. cleric-to-Lens), the audio key is the
 *  templateId itself — the extractor emits a dedicated `..._{templateId}.mp3`
 *  file voiced by the templateId's (race, gender) cast. This decouples the
 *  templateId-locked audio from the race+gender bucket file, so the bucket
 *  file can carry the default text safely when a non-templateId strand of the
 *  same race+gender ends up speaking line.text via fallback. */
export function variantKeyForSpeaker(
  speaker: { isParty: boolean; unitId?: string },
  matchedTemplateId?: string | null,
): string | null {
  if (!speaker.isParty) return null;
  if (matchedTemplateId) return matchedTemplateId;
  return getPartyVariantKey(speaker.unitId);
}

/** Default nameplates for each named NPC. Used as a fallback when a briefing
 *  doesn't set name/title on any line — typically because the NPC is a
 *  companion in that chapter and authors only fill name+title on the chapter
 *  where the NPC first speaks.
 *
 *  Per-line `title:` still wins (so Rhovane can evolve from "Silent Market,
 *  Kaeldavar" to "The Quiet, Kaeldavar" mid-arc); this only fills the gap
 *  when nothing in the current briefing names them. */
const NPC_NAMEPLATES: Record<string, { name: string; title: string }> = {
  npc_rhovane:          { name: 'Rhovane',                title: 'The Quiet, Kaeldavar' },
  npc_scholar_vesper:   { name: 'Vesper Thalane',         title: 'Dye-scholar, Violet Wells' },
  npc_scholar_aevryn:   { name: 'Aevryn',                 title: 'Vael Scholar-in-Residence, Irremar' },
  npc_steward_maren:    { name: 'Maren',                  title: 'Steward of Thornhaven' },
  npc_lens_aldric:      { name: 'Aldric',                 title: 'Faith of the Sun, Dawnfield Chapel' },
  npc_elder_thelys:     { name: 'Elder Thelys',           title: 'Vigil Elder' },
  npc_shipmaster_dariv: { name: 'Shipmaster Dariv Soll',  title: 'Thalassan Shipmaster' },
};

/** Look up the canonical name + title for an NPC. Within-briefing lines win
 *  (authors set title on the first appearance of each speaker in a chapter
 *  and the rest inherit it). Falls back to NPC_NAMEPLATES when nothing in
 *  the current briefing dialogue names them — e.g. Vesper as a companion in
 *  Ch 8 where Rhovane is the primary speaker. */
export function getNpcNameplate(
  briefing: NpcBriefing,
  spriteKey: string,
): { name: string; title: string } {
  let name = '';
  let title = '';
  for (const other of briefing.dialogue) {
    if (other.speaker !== 'npc') continue;
    const otherKey = other.spriteKey || briefing.spriteKey || '';
    if (otherKey !== spriteKey) continue;
    if (!name && other.name) name = other.name;
    if (!title && other.title) title = other.title;
    if (name && title) break;
  }
  const registry = NPC_NAMEPLATES[spriteKey];
  if (!name && registry) name = registry.name;
  if (!title && registry) title = registry.title;
  return { name, title };
}

/** Strip ElevenLabs v3 emotion/delivery tags from displayed text. Tags live
 *  inline in briefings.ts (e.g. "[grim] By dawn he holds it open") so they
 *  travel through to the voiceover generator, but never reach the screen. */
function stripVoTags(text: string): string {
  return text.replace(/\[[^\]]+\]\s*/g, '').replace(/\s+/g, ' ').trim();
}

export interface ResolvedDialogue {
  speaker: ResolvedSpeaker;
  text: string;
  /** templateId of the matched variant, when a templateId variant fired. Used
   *  to route audio to the dedicated `..._{templateId}.mp3` file. */
  matchedTemplateId?: string | null;
}

export function resolveSpeaker(briefing: NpcBriefing, line: DialogueLine): ResolvedDialogue {
  if (line.speaker === 'npc') {
    const lineKey = line.spriteKey || briefing.spriteKey || '';
    // Persist name + title across lines for the same NPC. Authors only set
    // these on the first appearance, so subsequent lines inherit the plate.
    const fallback = getNpcNameplate(briefing, lineKey);
    return {
      speaker: {
        name: line.name ?? fallback.name ?? 'Unknown',
        title: line.title ?? fallback.title ?? '',
        isParty: false,
        spriteKey: lineKey,
      },
      text: stripVoTags(line.text),
    };
  }

  // Party line — collect every variant whose condition matches a party member
  // (or has no condition), then pick one at random. Caller invokes resolveSpeaker
  // exactly once per page render, so the random pick stays stable across the
  // page's portrait, name, text, and audio.
  if (line.variants) {
    const matches: Array<{ unit: PartyUnit | null; text: string; templateId?: string }> = [];
    for (const variant of line.variants) {
      if (!variant.condition) {
        matches.push({ unit: getActiveParty()[0] ?? null, text: variant.text });
        continue;
      }
      const unit = findMatchingStrand(variant.condition);
      if (unit) matches.push({ unit, text: variant.text, templateId: variant.condition.templateId });
    }
    if (matches.length > 0) {
      // Prefer matches whose unit has a cast voice. Only fall back to uncast
      // strands when the entire match pool is silent — keeps voiced briefings
      // from randomly landing on a strand whose voice isn't recorded yet.
      const voiced = matches.filter(m => isUnitVoiced(m.unit));
      const pool = voiced.length > 0 ? voiced : matches;
      const pick = pool[Math.floor(Math.random() * pool.length)];
      const unit = pick.unit;
      return {
        speaker: {
          name: unit?.name ?? line.name ?? 'Companion',
          title: '',
          isParty: true,
          spriteKey: unit ? getPartyUnitSpriteKey(unit) : undefined,
          unitId: unit?.id,
        },
        text: stripVoTags(pick.text),
        matchedTemplateId: pick.templateId ?? null,
      };
    }
  }

  // No variants or none matched — pick the unit matching requiredCondition
  // (so race/class-gated lines come from that unit's portrait), or fall back
  // to a party member. Prefer voiced members; only fall back to party[0] when
  // the whole party is uncast.
  const party = getActiveParty();
  const required = line.requiredCondition ? findMatchingStrand(line.requiredCondition) : null;
  const voicedMember = party.find(isUnitVoiced) ?? null;
  const speaker = required ?? voicedMember ?? party[0];
  return {
    speaker: {
      name: line.name ?? speaker?.name ?? 'Companion',
      title: '',
      isParty: true,
      spriteKey: speaker ? getPartyUnitSpriteKey(speaker) : undefined,
      unitId: speaker?.id,
    },
    text: stripVoTags(line.text),
  };
}

/* ── Overlay lifecycle ── */

let activeOverlay: HTMLElement | null = null;
let activeResolve: (() => void) | null = null;
let escHandler: ((e: KeyboardEvent) => void) | null = null;

function closeActive(): void {
  if (!activeOverlay) return;
  const overlay = activeOverlay;
  overlay.classList.remove('is-visible');

  stopVoiceover();

  if (escHandler) {
    document.removeEventListener('keydown', escHandler);
    escHandler = null;
  }

  window.setTimeout(() => {
    overlay.remove();
    if (activeOverlay === overlay) {
      activeOverlay = null;
      const resolve = activeResolve;
      activeResolve = null;
      if (resolve) resolve();
    }
  }, 250);
}

/* ── Voiceover playback ──
 * Optional per-line MP3 narration. Files live at
 *   activity/public/voiceovers/{chapter}/{kind}_{originalIndex}.mp3                       (NPC)
 *   activity/public/voiceovers/{chapter}/{kind}_{originalIndex}_{race}_{gender}.mp3       (party)
 * where audioBase is "ch{N}_briefing" or "ch{N}_debrief"; chapter+kind are split
 * out into folder + filename. originalIndex is the line's position in the
 * unfiltered briefing.dialogue array (so requiredCondition changes don't shift
 * filenames). Missing files are silently skipped — voiceover is purely additive. */

let activeAudio: HTMLAudioElement | null = null;

export function stopVoiceover(): void {
  if (!activeAudio) return;
  activeAudio.pause();
  activeAudio.src = '';
  activeAudio = null;
}

export function playVoiceover(
  audioBase: string,
  originalIndex: number,
  variantKey?: string | null,
): void {
  stopVoiceover();
  const m = audioBase.match(/^(ch\d+)_(briefing|debrief|decision)$/);
  if (!m) {
    console.warn(`[voiceover] invalid audioBase "${audioBase}" — expected ch{N}_briefing/debrief/decision`);
    return;
  }
  const [, chapter, kind] = m;
  const base = import.meta.env.BASE_URL ?? '/';
  const suffix = variantKey ? `_${variantKey}` : '';
  const url = `${base}voiceovers/${chapter}/${kind}_${originalIndex}${suffix}.mp3`;
  const audio = new Audio(url);
  audio.crossOrigin = 'anonymous'; // required for MediaElementSource on cross-origin assets
  audio.addEventListener('error', () => {
    console.info(`[voiceover] no file at ${url} (skipping)`);
  });
  activeAudio = audio;
  // Route through AudioBus so all voices pick up the shared room IR — glues
  // different ElevenLabs voices into one acoustic space. Must be called
  // before .play() so the MediaElementSource captures the stream.
  audioBus.routeVoiceElement(audio);
  audio.play()
    .then(() => { console.info(`[voiceover] playing ${url}`); })
    .catch((err) => {
      console.warn(`[voiceover] play rejected for ${url}:`, err?.name || err);
      if (activeAudio === audio) activeAudio = null;
    });
}

/* ── Typewriter helpers ── */

const TYPEWRITER_SPEED = 18; // ms per character (~55 chars/sec)

interface TypewriterState {
  timer: number | null;
  typing: boolean;
  fullText: string;
}

function startTypewriter(
  state: TypewriterState,
  textEl: HTMLElement,
  text: string,
  onComplete: () => void,
): void {
  state.fullText = text;
  state.typing = true;
  textEl.textContent = '';
  let i = 0;

  const tick = () => {
    if (i < text.length) {
      textEl.textContent = text.substring(0, ++i);
      state.timer = window.setTimeout(tick, TYPEWRITER_SPEED);
    } else {
      state.typing = false;
      state.timer = null;
      onComplete();
    }
  };
  tick();
}

function completeTypewriter(state: TypewriterState, textEl: HTMLElement): void {
  if (state.timer) {
    clearTimeout(state.timer);
    state.timer = null;
  }
  textEl.textContent = state.fullText;
  state.typing = false;
}

function killTypewriter(state: TypewriterState): void {
  if (state.timer) {
    clearTimeout(state.timer);
    state.timer = null;
  }
}

/* ── Core dialogue scene (shared by briefing + debrief) ── */

async function runDialogueScene(briefing: NpcBriefing, bgUrl: string | null, audioBase: string | null = null): Promise<void> {
  while (activeOverlay) {
    await new Promise<void>(res => window.setTimeout(res, 50));
  }

  const party = getActiveParty();
  // Preserve original (pre-filter) index so voiceover filenames are stable
  // across requiredCondition changes.
  const dialogueWithIdx = briefing.dialogue
    .map((line, originalIdx) => ({ line, originalIdx }))
    .filter(({ line }) => {
      if (!line.requiredCondition) return true;
      return party.some(u => matchCondition(u, line.requiredCondition!));
    });
  if (!dialogueWithIdx.length) return;
  const dialogue = dialogueWithIdx.map(d => d.line);
  const originalIndices = dialogueWithIdx.map(d => d.originalIdx);

  let pageIndex = 0;
  const tw: TypewriterState = { timer: null, typing: false, fullText: '' };

  // Only render the NPC sprite if any dialogue line is actually spoken by an NPC.
  // Some "debriefs" (Ch2–Ch4) are party-only conversations that still carry a
  // spriteKey for portrait fallback; we must NOT show that sprite on the stage.
  const hasNpcLine = dialogue.some(l => l.speaker === 'npc');
  const first = resolveSpeaker(briefing, dialogue[0]);
  const firstKey = first.speaker.spriteKey || briefing.spriteKey || '';
  // Collect every distinct NPC sprite — when a chapter has 2+ NPCs trading
  // lines, render them all at once and dim the silent ones instead of swapping
  // a single sprite source.
  const npcKeys = hasNpcLine ? collectNpcSpriteKeys(briefing, dialogue) : [];
  const isMultiNpc = npcKeys.length >= 2;
  const npcStageHTML = hasNpcLine ? buildNpcStageHTML(npcKeys, firstKey) : '';
  const firstPortraitSrc = getPortraitUrl(firstKey);
  const firstTuning = getPortraitTuning(firstKey);

  // Party sprites for the left side — use the same renderUnitPortrait component
  // the hub uses so per-sprite scale + bottom-anchored origin match. CSS scopes
  // height to .nb-party .unit-portrait--lg { height: 45cqh } (mirrors hub).
  const partyHTML = party.map(u => {
    const key = getPartyUnitSpriteKey(u);
    if (!getSpriteSrc(key)) return '';
    return `<div class="party-member" data-unit-id="${u.id}">
      <div class="party-portrait-wrap">
        ${renderUnitPortrait({ spriteKey: key, name: u.name, size: 'lg', scale: PLAYER_SPRITES[key]?.scale })}
      </div>
    </div>`;
  }).join('');

  const overlay = document.createElement('div');
  const extraClass = bgUrl ? '' : ' nb-debrief';
  overlay.className = `npc-briefing-overlay${extraClass} nb-speaker-${first.speaker.isParty ? 'party' : 'npc'}`;
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');

  const bgStyle = bgUrl ? ` style="background-image: url('${bgUrl}')"` : '';

  const multiClass = isMultiNpc ? ' nb-npc--multi' : '';
  overlay.innerHTML = `
    <div class="nb-scene"${bgStyle}>
      <div class="nb-party">${partyHTML}</div>
      ${npcStageHTML ? `<div class="nb-npc${multiClass}">${npcStageHTML}</div>` : ''}
      <div class="nb-dialogue">
        <div class="nb-header">
          <div class="nb-portrait">
            <img class="nb-portrait-img" src="${firstPortraitSrc}" alt="" draggable="false"
                 style="${buildPortraitStyle(firstTuning)}" />
          </div>
          <div class="nb-speaker-info">
            <span class="nb-name"></span>
            <span class="nb-title"></span>
          </div>
        </div>
        <div class="nb-body">
          <div class="nb-text"></div>
        </div>
        <div class="nb-footer">
          <span class="nb-continue">${dialogue.length === 1 ? 'Tap to close &#9656;' : 'Tap to continue &#9656;'}</span>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  activeOverlay = overlay;

  const promise = new Promise<void>((resolve) => { activeResolve = resolve; });

  const dialogueEl = overlay.querySelector('.nb-dialogue') as HTMLElement;
  const nameEl = overlay.querySelector('.nb-name') as HTMLElement;
  const titleEl = overlay.querySelector('.nb-title') as HTMLElement;
  const textEl = overlay.querySelector('.nb-text') as HTMLElement;
  const portraitImg = overlay.querySelector('.nb-portrait-img') as HTMLImageElement;
  const continueEl = overlay.querySelector('.nb-continue') as HTMLElement;
  const npcEl = overlay.querySelector('.nb-npc') as HTMLElement | null;

  // Set initial speaker info
  nameEl.textContent = first.speaker.name;
  nameEl.classList.toggle('nb-name--party', first.speaker.isParty);
  titleEl.textContent = first.speaker.title;
  titleEl.style.display = first.speaker.title ? '' : 'none';

  // Highlight which NPC sprite is currently speaking. Single-NPC scenes also
  // get this — it's a no-op (single sprite always has .is-speaking).
  const highlightNpcSpeaker = (key: string) => {
    if (!npcEl) return;
    npcEl.querySelectorAll('.nb-npc-sprite').forEach(el => el.classList.remove('is-speaking'));
    npcEl.querySelector(`.nb-npc-sprite[data-sprite-key="${key}"]`)?.classList.add('is-speaking');
  };

  // Party speaker highlight
  const highlightPartySpeaker = (unitId?: string) => {
    overlay.querySelectorAll('.nb-party .party-member.nb-speaking').forEach(el => el.classList.remove('nb-speaking'));
    if (unitId) {
      overlay.querySelector(`.nb-party .party-member[data-unit-id="${unitId}"]`)?.classList.add('nb-speaking');
    }
  };
  if (first.speaker.isParty) highlightPartySpeaker(first.speaker.unitId);

  const showContinue = () => continueEl.classList.add('is-visible');

  /** Pre-measure full text height so the box stays stable during typewriter. */
  const preMeasure = (text: string) => {
    textEl.style.visibility = 'hidden';
    textEl.textContent = text;
    textEl.style.minHeight = `${textEl.scrollHeight}px`;
    textEl.textContent = '';
    textEl.style.visibility = '';
  };

  // Tap handler — on the full overlay so tapping anywhere advances
  overlay.addEventListener('click', () => {
    if (tw.typing) {
      completeTypewriter(tw, textEl);
      showContinue();
      audioBus.playSfx('tick');
      return;
    }

    pageIndex++;
    if (pageIndex >= dialogue.length) {
      killTypewriter(tw);
      closeActive();
      audioBus.playSfx('back');
      return;
    }
    audioBus.playSfx('click');

    const resolved = resolveSpeaker(briefing, dialogue[pageIndex]);
    const resolvedKey = resolved.speaker.spriteKey || dialogue[pageIndex].spriteKey || briefing.spriteKey || '';

    dialogueEl.classList.add('nb-text-fade');

    window.setTimeout(() => {
      // Portrait
      const newSrc = getPortraitUrl(resolvedKey);
      if (newSrc) portraitImg.src = newSrc;
      applyPortraitTuning(portraitImg, getPortraitTuning(resolvedKey));

      // Name / title
      nameEl.textContent = resolved.speaker.name;
      nameEl.classList.toggle('nb-name--party', resolved.speaker.isParty);
      titleEl.textContent = resolved.speaker.title;
      titleEl.style.display = resolved.speaker.title ? '' : 'none';

      // Highlight whichever NPC sprite is now speaking. All distinct NPC
      // sprites are already on stage from initial render — we just toggle
      // .is-speaking. Falls back to briefing.spriteKey when a line omits its
      // own (matches the keys collected at render time).
      if (!resolved.speaker.isParty) {
        const lineKey = dialogue[pageIndex].spriteKey || briefing.spriteKey || resolvedKey;
        highlightNpcSpeaker(lineKey);
      }

      // Speaker dimming + party highlight
      overlay.classList.toggle('nb-speaker-npc', !resolved.speaker.isParty);
      overlay.classList.toggle('nb-speaker-party', resolved.speaker.isParty);
      highlightPartySpeaker(resolved.speaker.isParty ? resolved.speaker.unitId : undefined);

      // Continue label
      if (pageIndex === dialogue.length - 1) {
        continueEl.innerHTML = 'Tap to close &#9656;';
      }

      // Fade in + pre-measure + typewrite
      dialogueEl.classList.remove('nb-text-fade');
      preMeasure(resolved.text);
      if (audioBase) playVoiceover(audioBase, originalIndices[pageIndex], variantKeyForSpeaker(resolved.speaker, resolved.matchedTemplateId));
      startTypewriter(tw, textEl, resolved.text, showContinue);
    }, 120);
  });

  // ESC to dismiss
  escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      killTypewriter(tw);
      closeActive();
    }
  };
  document.addEventListener('keydown', escHandler);

  // Animate in
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      // Pre-measure AFTER layout is guaranteed but BEFORE fade-in
      preMeasure(first.text);
      overlay.classList.add('is-visible');
      overlay.querySelector('.nb-party')?.classList.add('is-visible');
      overlay.querySelector('.nb-npc')?.classList.add('is-visible');
      dialogueEl?.classList.add('is-visible');
      setTimeout(() => {
        if (audioBase) playVoiceover(audioBase, originalIndices[0], variantKeyForSpeaker(first.speaker, first.matchedTemplateId));
        startTypewriter(tw, textEl, first.text, showContinue);
      }, 380);
    });
  });

  await promise;
}

/* ── Show post-crawl debrief dialogue (over combat backdrop) ── */

export async function showDebriefDialogue(briefing: NpcBriefing, stepIndex?: number): Promise<void> {
  if (!briefing.dialogue.length) return;
  const audioBase = stepIndex != null ? `ch${stepIndex}_debrief` : null;
  await runDialogueScene(briefing, null, audioBase);
}

/* ── Decision scene (Ch5 Orien fork, reusable for future forks) ── */

export async function showDecisionScene(
  decision: DecisionConfig,
  crawlId: string,
): Promise<'kill' | 'spare'> {
  while (activeOverlay) {
    await new Promise<void>(res => window.setTimeout(res, 50));
  }

  const spriteSrc = getFullSpriteUrl(decision.spriteKey);

  const overlay = document.createElement('div');
  overlay.className = 'npc-briefing-overlay nb-debrief nb-decision';
  overlay.innerHTML = `
    <div class="nb-scene">
      <img class="nb-decision-center-sprite" src="${spriteSrc ?? ''}" alt="" draggable="false" />
      <div class="nb-decision-goldwash"></div>
      <div class="nb-decision-flash"></div>
      <div class="nb-decision-bottom-stack">
        <div class="nb-dialogue" style="opacity: 0; transition: opacity 400ms ease-out;">
          <div class="nb-header">
            <div class="nb-speaker-info">
              <span class="nb-name">${decision.npcName}</span>
              ${decision.npcTitle ? `<span class="nb-title">${decision.npcTitle}</span>` : ''}
            </div>
          </div>
          <div class="nb-body">
            <div class="nb-text"></div>
          </div>
        </div>
        <div class="nb-choices-row" role="group" aria-label="Decision"></div>
      </div>
      <div class="nb-decision-blackout"></div>
    </div>
  `;
  document.body.appendChild(overlay);
  activeOverlay = overlay;

  await new Promise<void>(r => requestAnimationFrame(() => r()));
  overlay.classList.add('is-visible');

  const scene = overlay.querySelector('.nb-scene') as HTMLElement;
  const sprite = overlay.querySelector('.nb-decision-center-sprite') as HTMLElement;
  const dialogue = overlay.querySelector('.nb-dialogue') as HTMLElement;
  const textEl = overlay.querySelector('.nb-text') as HTMLElement;
  const choicesRow = overlay.querySelector('.nb-choices-row') as HTMLElement;
  const flash = overlay.querySelector('.nb-decision-flash') as HTMLElement;
  const goldwash = overlay.querySelector('.nb-decision-goldwash') as HTMLElement;
  const blackout = overlay.querySelector('.nb-decision-blackout') as HTMLElement;

  // Fade in dialog box + typewriter opening line
  await new Promise<void>(r => window.setTimeout(r, 300));
  dialogue.style.opacity = '1';
  const tw: TypewriterState = { timer: null, typing: false, fullText: '' };
  await new Promise<void>(resolve => {
    startTypewriter(tw, textEl, decision.openingLine, resolve);
  });

  // Render choice cards
  choicesRow.innerHTML = decision.choices.map((choice, i) => `
    <button class="nb-choice-card" data-choice-idx="${i}" type="button">
      <span class="nb-choice-label">${choice.label}</span>
      <span class="nb-choice-preview">${choice.preview}</span>
    </button>
  `).join('');
  await new Promise<void>(r => requestAnimationFrame(() => r()));
  choicesRow.classList.add('is-visible');

  // Wait for click
  const picked: DecisionChoice = await new Promise(resolve => {
    const buttons = choicesRow.querySelectorAll<HTMLButtonElement>('.nb-choice-card');
    buttons.forEach((btn, idx) => {
      btn.addEventListener('click', () => {
        buttons.forEach(b => { b.disabled = true; });
        audioBus.playSfx('select');
        resolve(decision.choices[idx]);
      });
    });
  });

  // Fire-and-forget backend grant (don't block on network — the moment has its own pace).
  // If it fails, we log but proceed — the player experience is not held hostage to HTTP latency.
  APIManager.resolveCampaignDecision(crawlId, picked.id).catch(err => {
    console.error('[decision] grant failed:', err);
  });

  // Hide choice cards while animation plays
  choicesRow.classList.remove('is-visible');

  // Execute path: white flash + shake + red drain + blackout
  // Spare path: followup line + gentle fade + gold wash
  if (picked.animation === 'execute') {
    flash.classList.add('is-firing');
    scene.classList.add('nb-decision-shake');
    sprite.classList.add('is-executing');
    await new Promise<void>(r => window.setTimeout(r, 800));
    // AUDIO: blade-scrape -> silence -> muted thud
    blackout.classList.add('is-firing');
    await new Promise<void>(r => window.setTimeout(r, 400));
  } else {
    // Spare: typewrite followup line first
    if (picked.followupLine) {
      textEl.textContent = '';
      await new Promise<void>(resolve => {
        startTypewriter(tw, textEl, picked.followupLine!, resolve);
      });
      await new Promise<void>(r => window.setTimeout(r, 400));
    }
    goldwash.classList.add('is-firing');
    sprite.classList.add('is-sparing');
    // AUDIO: low gold-bell tone on click
    await new Promise<void>(r => window.setTimeout(r, 900));
  }

  // Exit — remove overlay
  overlay.classList.remove('is-visible');
  killTypewriter(tw);
  await new Promise<void>(r => window.setTimeout(r, 200));
  overlay.remove();
  if (activeOverlay === overlay) activeOverlay = null;

  return picked.id;
}
